/**
 * Transform DRF practice full-detail response → GraphQL getPractice shape.
 *
 * The DRF endpoint returns a flat structure with organisation fields
 * directly on the practice object, and currentAccreditation as a single
 * object. Components expect the Hasura shape with nested organisationPtr
 * and accreditations array.
 */

/**
 * Transform the DRF full-detail response to match the Hasura getPractice shape.
 * @param {object} drf - camelCased response from GET /api/web.practice/{id}/full-detail/
 * @returns {object} - shape matching the existing getPractice GraphQL response
 */
export function transformPracticeDetail(drf) {
  if (!drf) return null

  const accred = drf.currentAccreditation

  // The AccreditationContact form binds to the flat accredContact* fields on
  // the practice, but legacy practices only carry that data on the current
  // accreditation's nominated contact (the flat columns are null). Fall back to
  // the nested contact when the flat fields are empty so the form populates; a
  // subsequent save writes the flat fields back. Phone defaults to the
  // { countryCode, phone } shape the phone-number component expects.
  const nominated = accred && accred.nominatedAccreditationContact

  // Build the organisationPtr sub-object that PracticeDetails.vue expects
  const organisationPtr = {
    id: drf.id,
    abn: drf.abn,
    billingAbn: drf.billingAbn,
    billingName: drf.billingName,
    billingContactName: drf.billingContactName,
    billingContactEmail: drf.billingContactEmail,
    // Practices created before this field existed come back null, and
    // PhoneNumber dereferences .phone on its value, so default the shape.
    billingContactMobile: drf.billingContactMobile || { countryCode: 'AU', phone: '' },
    email: drf.email,
    isBillingSameAsPhysical: drf.isBillingSameAsPhysical,
    isPostalSameAsPhysical: drf.isPostalSameAsPhysical,
    legalEntity: drf.legalEntity,
    legalName: drf.legalName,
    structure: drf.structure,
    structureOther: drf.structureOther,
    website: drf.website,
    archived: false,
    fax: drf.fax,
    phone: drf.phone,
    physicalAddress: drf.physicalAddress,
    billingAddress: drf.billingAddress,
    postalAddress: drf.postalAddress,
  }

  // Build accreditations array with visits sub-arrays
  let accreditations = []
  if (accred) {
    accreditations = [{
      ...accred,
      // visits array (GraphQL returns visits as array, limit 1)
      visits: accred.finalVisit ? [accred.finalVisit] : [],
      // preferred_days_visits alias
      preferredDaysVisits: accred.preferredDaysVisit ? [accred.preferredDaysVisit] : [],
      // pref_and_draft_visits alias
      prefAndDraftVisits: accred.prefAndDraftVisit ? [accred.prefAndDraftVisit] : [],
      // certificates array
      certificates: accred.currentCertificate ? [accred.currentCertificate] : [],
    }]
  }

  return {
    ...drf,
    organisationPtrId: drf.id,
    organisationPtr,
    accreditations,
    // Flat accreditation-contact fields the form binds to, with a fallback to
    // the accreditation's nominated contact for legacy practices.
    accredContactFirstName: drf.accredContactFirstName || (nominated && nominated.firstName) || '',
    accredContactLastName: drf.accredContactLastName || (nominated && nominated.lastName) || '',
    accredContactEmail: drf.accredContactEmail || (nominated && nominated.email) || '',
    // Strip the nested contact's phone `id` so a save creates a fresh phone for
    // the practice's own accred_contact_phone OneToOne rather than mutating /
    // hijacking the nominated contact's Phone record.
    accredContactPhone: drf.accredContactPhone
      || (nominated && nominated.phone && { countryCode: nominated.phone.countryCode, phone: nominated.phone.phone })
      || { countryCode: 'AU', phone: '' },
    // Aggregate shapes that components read
    accreditationsAggregate: {
      aggregate: { count: drf.accreditationsCount || 0 },
    },
    reapplicationsAggregate: {
      aggregate: { count: drf.reapplicationsDueCount || 0 },
    },
    reapplicationsPaymentAggregate: {
      aggregate: { count: drf.reapplicationsPaymentPendingCount || 0 },
    },
  }
}
